import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Sync as SyncIcon,
  Cloud,
  CloudOff,
  CloudDone,
  Settings,
  Refresh,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  History,
  Speed,
  Computer,
  Link as LinkIcon,
  LinkOff,
  PlayArrow,
} from '@mui/icons-material';
import { isSatellite, isMainLab, getLocationConfig } from '../../config/location.config';
import api from '../../services/api';

// Types
interface SyncConfig {
  id: string;
  location_code: string;
  location_type: string;
  sync_enabled: boolean;
  sync_target_url: string | null;
  sync_interval_ms: number;
  last_sync_at: string | null;
  sync_status: string;
  last_sync_error: string | null;
  pending_sync_count: number;
  failed_sync_count: number;
}

interface SyncStatus {
  enabled: boolean;
  locationType: string;
  locationCode: string;
  targetUrl: string;
  lastSyncAt: string | null;
  pendingCount: number;
  failedCount: number;
  isSyncing: boolean;
}

interface Satellite {
  location_code: string;
  location_name: string;
  last_sync_at: string | null;
  sync_status: string;
  pending_count: number;
  api_endpoint: string;
}

interface SyncLog {
  id: string;
  entity_type: string;
  operation: string;
  status: string;
  from_location: string;
  created_at: string;
  synced_at: string | null;
  error_message: string | null;
}

const SyncConfiguration: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Config state
  const [config, setConfig] = useState<SyncConfig | null>(null);
  const [status, setStatus] = useState<SyncStatus | null>(null);
  
  // Form state (for satellite)
  const [targetUrl, setTargetUrl] = useState('');
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [syncInterval, setSyncInterval] = useState(5); // minutes
  
  // Main lab state
  const [satellites, setSatellites] = useState<Satellite[]>([]);
  
  // Logs state
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [showLogs, setShowLogs] = useState(false);
  
  // Test result
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    latency_ms?: number;
  } | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const locationConfig = getLocationConfig();
  const isSatelliteSite = isSatellite();
  const isMainLabSite = isMainLab();

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load sync config
      const configData = await api.get('/sync/config');
      setConfig(configData);
      
      if (configData) {
        setTargetUrl(configData.sync_target_url || '');
        setSyncEnabled(configData.sync_enabled || false);
        setSyncInterval(Math.round((configData.sync_interval_ms || 300000) / 60000));
      }

      // Load sync status
      const statusData = await api.get('/sync/status');
      setStatus(statusData);

      // Load satellites if main lab
      if (isMainLabSite) {
        const satData = await api.get('/sync/satellites');
        setSatellites(satData.satellites || []);
      }

      // Load recent logs
      const logsData = await api.get('/sync/history?limit=20');
      setLogs(logsData.logs || []);

    } catch (err: any) {
      console.error('Failed to load sync config:', err);
      setError(err.message || 'Failed to load sync configuration');
    } finally {
      setLoading(false);
    }
  }, [isMainLabSite]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Save configuration
  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await api.put('/sync/config', {
        sync_enabled: syncEnabled,
        sync_target_url: targetUrl,
        sync_interval_ms: syncInterval * 60000,
      });

      setSuccess('Configuration saved successfully');
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Test connection
  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      setError(null);

      const result = await api.post('/sync/test-connection', {
        target_url: targetUrl,
      });

      setTestResult(result);
      
      if (result.success) {
        setSuccess(`Connection successful! Latency: ${result.latency_ms}ms`);
      }
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.message || 'Connection test failed',
      });
      setError(err.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  // Trigger manual sync
  const handleTriggerSync = async () => {
    try {
      setSyncing(true);
      setError(null);

      const result = await api.post('/sync/trigger');
      setSuccess(`Sync completed: ${result.pushed} pushed, ${result.pulled} pulled`);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'CONNECTED':
      case 'SYNCED':
        return 'success';
      case 'SYNCING':
        return 'info';
      case 'PENDING':
        return 'warning';
      case 'ERROR':
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'CONNECTED':
      case 'SYNCED':
        return <CloudDone color="success" />;
      case 'SYNCING':
        return <SyncIcon color="info" className="rotating" />;
      case 'ERROR':
      case 'FAILED':
        return <CloudOff color="error" />;
      default:
        return <Cloud color="disabled" />;
    }
  };

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR');
  };

  // Format interval
  const formatInterval = (ms: number) => {
    const minutes = Math.round(ms / 60000);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.round(minutes / 60);
    return `${hours} h`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <SyncIcon fontSize="large" />
            Configuration de Synchronisation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isSatelliteSite ? 'Configuration de la connexion au laboratoire principal' : 'Gestion des sites satellites connectés'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip 
            icon={<Computer />}
            label={locationConfig.name}
            color="primary"
            variant="outlined"
          />
          <Chip 
            label={isSatelliteSite ? 'Site Satellite' : 'Laboratoire Principal'}
            color={isSatelliteSite ? 'warning' : 'success'}
          />
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* ====================== */}
        {/* SATELLITE SITE UI */}
        {/* ====================== */}
        {isSatelliteSite && (
          <>
            {/* Connection Status Card */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {getStatusIcon(config?.sync_status || 'DISCONNECTED')}
                    État de la Connexion
                  </Typography>
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Chip 
                      label={config?.sync_status || 'DISCONNECTED'}
                      color={getStatusColor(config?.sync_status || '')}
                      size="large"
                      sx={{ fontSize: '1.1rem', py: 2, px: 3 }}
                    />
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2">
                      <strong>Dernière sync:</strong> {formatDate(config?.last_sync_at || null)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>En attente:</strong> {config?.pending_sync_count || 0} élément(s)
                    </Typography>
                    <Typography variant="body2">
                      <strong>Échecs:</strong> {config?.failed_sync_count || 0} élément(s)
                    </Typography>
                  </Box>
                  {config?.last_sync_error && (
                    <Alert severity="error" sx={{ mt: 2, fontSize: '0.75rem' }}>
                      {config.last_sync_error}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Configuration Card */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <Settings />
                    Configuration du Laboratoire Principal
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField
                      label="URL du Laboratoire Principal"
                      placeholder="http://100.x.x.x:3001"
                      value={targetUrl}
                      onChange={(e) => setTargetUrl(e.target.value)}
                      fullWidth
                      helperText="Adresse IP Tailscale du serveur Marrakech (ex: http://100.64.0.1:3001)"
                      InputProps={{
                        startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <FormControl sx={{ minWidth: 150 }}>
                        <InputLabel>Intervalle de sync</InputLabel>
                        <Select
                          value={syncInterval}
                          onChange={(e) => setSyncInterval(Number(e.target.value))}
                          label="Intervalle de sync"
                        >
                          <MenuItem value={1}>1 minute</MenuItem>
                          <MenuItem value={2}>2 minutes</MenuItem>
                          <MenuItem value={5}>5 minutes</MenuItem>
                          <MenuItem value={10}>10 minutes</MenuItem>
                          <MenuItem value={15}>15 minutes</MenuItem>
                          <MenuItem value={30}>30 minutes</MenuItem>
                          <MenuItem value={60}>1 heure</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControlLabel
                        control={
                          <Switch
                            checked={syncEnabled}
                            onChange={(e) => setSyncEnabled(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Activer la synchronisation automatique"
                      />
                    </Box>

                    {/* Test Result */}
                    {testResult && (
                      <Alert 
                        severity={testResult.success ? 'success' : 'error'}
                        icon={testResult.success ? <CheckCircle /> : <ErrorIcon />}
                      >
                        {testResult.message}
                        {testResult.latency_ms && ` (Latence: ${testResult.latency_ms}ms)`}
                      </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={handleTestConnection}
                        disabled={!targetUrl || testing}
                        startIcon={testing ? <CircularProgress size={20} /> : <Speed />}
                      >
                        {testing ? 'Test en cours...' : 'Tester la Connexion'}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSaveConfig}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={20} /> : <CheckCircle />}
                      >
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Manual Sync Card */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SyncIcon />
                        Synchronisation Manuelle
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Déclencher une synchronisation immédiate avec le laboratoire principal
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleTriggerSync}
                      disabled={!syncEnabled || !targetUrl || syncing}
                      startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                      size="large"
                    >
                      {syncing ? 'Synchronisation...' : 'Synchroniser Maintenant'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* ====================== */}
        {/* MAIN LAB UI */}
        {/* ====================== */}
        {isMainLabSite && (
          <>
            {/* Status Overview */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Cloud />
                    Sites Satellites Connectés
                  </Typography>

                  {satellites.length === 0 ? (
                    <Alert severity="info">
                      Aucun site satellite configuré. Les satellites se connecteront automatiquement une fois configurés.
                    </Alert>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Site</TableCell>
                            <TableCell>Code</TableCell>
                            <TableCell>Statut</TableCell>
                            <TableCell>Dernière Sync</TableCell>
                            <TableCell>En Attente</TableCell>
                            <TableCell>Endpoint</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {satellites.map((sat) => (
                            <TableRow key={sat.location_code}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {getStatusIcon(sat.sync_status)}
                                  <Typography fontWeight="bold">{sat.location_name}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip label={sat.location_code} size="small" />
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={sat.sync_status || 'UNKNOWN'}
                                  color={getStatusColor(sat.sync_status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{formatDate(sat.last_sync_at)}</TableCell>
                              <TableCell>
                                {sat.pending_count > 0 ? (
                                  <Chip 
                                    label={sat.pending_count}
                                    color="warning"
                                    size="small"
                                  />
                                ) : (
                                  <Chip label="0" size="small" />
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                  {sat.api_endpoint || '-'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Sync Stats */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>Statistiques</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>Sites connectés:</Typography>
                      <Chip label={satellites.length} color="primary" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>Total en attente:</Typography>
                      <Chip 
                        label={satellites.reduce((acc, s) => acc + s.pending_count, 0)}
                        color={satellites.reduce((acc, s) => acc + s.pending_count, 0) > 0 ? 'warning' : 'default'}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>Status serveur:</Typography>
                      <Chip label={status?.isSyncing ? 'SYNCING' : 'READY'} color="success" />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Configuration Info */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Settings />
                    Configuration du Serveur
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Le laboratoire principal reçoit automatiquement les données des sites satellites. 
                    Aucune configuration supplémentaire n'est nécessaire.
                  </Alert>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2">
                      <strong>Code location:</strong> {status?.locationCode || locationConfig.code}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Type:</strong> {status?.locationType || 'MAIN_LAB'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Endpoint:</strong> <code>/api/v1/sync/receive</code>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* ====================== */}
        {/* SYNC LOGS (Both) */}
        {/* ====================== */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <History />
                  Historique de Synchronisation
                </Typography>
                <IconButton onClick={loadData}>
                  <Refresh />
                </IconButton>
              </Box>

              {logs.length === 0 ? (
                <Alert severity="info">Aucun historique de synchronisation</Alert>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Opération</TableCell>
                        <TableCell>Origine</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell>Erreur</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {logs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {formatDate(log.created_at)}
                          </TableCell>
                          <TableCell>
                            <Chip label={log.entity_type} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={log.operation}
                              size="small"
                              color={log.operation === 'CREATE' ? 'success' : log.operation === 'UPDATE' ? 'info' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{log.from_location}</TableCell>
                          <TableCell>
                            <Chip 
                              label={log.status}
                              size="small"
                              color={getStatusColor(log.status)}
                            />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200, fontSize: '0.7rem', color: 'error.main' }}>
                            {log.error_message || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* CSS for rotating icon */}
      <style>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 2s linear infinite;
        }
      `}</style>
    </Box>
  );
};

export default SyncConfiguration;
